import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * About page animations
 * - Hero image: Phase 1 move to center, Phase 2 scale up from center
 * - Tech grid staggered reveal
 */
export function initAboutPage() {
    const hero = document.querySelector('.about-hero');
    if (!hero) return;

    // Elements
    const imgWrapper = hero.querySelector('.about-hero-img-wrapper');
    const heroContent = hero.querySelector('.about-hero-col--content');
    const pageTitle = hero.querySelector('.page-title');
    const overlayText = hero.querySelector('.about-hero-overlay-text');
    const img = imgWrapper?.querySelector('img');

    if (imgWrapper && img) {
        // Get initial position from layout
        const rect = imgWrapper.getBoundingClientRect();
        const startWidth = rect.width;
        const startHeight = rect.height;

        // Starting position (desktop: 25% left, 35% top)
        const isMobile = window.innerWidth < 992;
        const startLeft = isMobile ? '50%' : '17%';
        const startTop = isMobile ? '100%' : '45%';
        const startXPercent = isMobile ? -50 : 0;
        const startYPercent = isMobile ? -50 : 0;

        // Passepartout: 1rem = 16px
        const passepartout = 16;

        // Final dimensions
        const finalWidth = window.innerWidth - passepartout * 2;
        const finalHeight = window.innerHeight - passepartout * 2;

        // Calculate scale factors
        const scaleX = finalWidth / startWidth;
        const scaleY = finalHeight / startHeight;
        const scaleFactor = Math.max(scaleX, scaleY);

        // Create timeline with two phases
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: hero,
                start: 'top top',
                end: '+=200%',
                scrub: 1.5,
                pin: true,
                pinSpacing: true
            }
        });

        // Set initial state with GPU acceleration
        gsap.set(imgWrapper, {
            position: 'fixed',
            left: startLeft,
            top: startTop,
            xPercent: startXPercent,
            yPercent: startYPercent,
            width: startWidth,
            height: startHeight,
            willChange: 'transform',
            force3D: true,
            backfaceVisibility: 'hidden'
        });

        // Phase 1 (0 to 0.35): Move to center at same size
        tl.to(imgWrapper, {
            left: '50%',
            top: '50%',
            xPercent: -50,
            yPercent: -50,
            duration: 0.35,
            ease: 'power2.out'
        }, 0);

        // Fade out content
        if (heroContent) {
            tl.to(heroContent, {
                opacity: 0,
                x: 50,
                duration: 0.2
            }, 0);
        }

        // Fade out page title
        if (pageTitle) {
            tl.to(pageTitle, {
                opacity: 0,
                y: -30,
                duration: 0.2
            }, 0);
        }

        // Phase 2 (0.35 to 0.8): Scale up from center (GPU accelerated)
        tl.to(imgWrapper, {
            scale: scaleFactor,
            borderRadius: '0px',
            duration: 0.45,
            ease: 'power2.inOut'
        }, 0.35);

        // Show overlay text
        if (overlayText) {
            tl.to(overlayText, {
                opacity: 1,
                duration: 0.15
            }, 0.3);

            tl.to(overlayText, {
                opacity: 0,
                duration: 0.15
            }, 0.65);
        }

        // Add subtle blur
        tl.to(img, {
            filter: 'blur(2px)',
            duration: 0.3
        }, 0.4);
    }

    // Initialize About Info section
    initAboutInfo();

    // Initialize About Reveal section
    initAboutReveal();
}

/**
 * About Info section - Scrolling paragraphs animation
 */
function initAboutInfo() {
    const root = document.querySelector('.about-info');
    if (!root) return;

    const pinHeight = root.querySelector('.about-info-pin');
    const container = root.querySelector('.container-fluid');
    const paragraphs = root.querySelectorAll('.about-info-paragraph');

    if (!pinHeight || !container || paragraphs.length === 0) return;

    // Wrap words in spans for animation
    paragraphs.forEach(paragraph => {
        wrapWordsInSpan(paragraph);
    });

    // Pin the container while scrolling through pin-height
    ScrollTrigger.create({
        trigger: pinHeight,
        start: 'top top',
        end: 'bottom bottom',
        pin: container
    });

    // Create timeline for paragraph transitions
    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: pinHeight,
            start: 'top top',
            end: 'bottom bottom',
            scrub: true
        }
    });

    // Animate paragraphs
    paragraphs.forEach((paragraph, index) => {
        if (paragraphs[index + 1]) {
            // Disappear current paragraph words
            tl.to(paragraph.querySelectorAll('.about-info-word span'), {
                y: '100%',
                duration: 1,
                stagger: 0.2,
                ease: 'power4.in'
            });
            // Appear next paragraph words (at the same time)
            tl.to(paragraphs[index + 1].querySelectorAll('.about-info-word span'), {
                y: '0%',
                duration: 1,
                delay: 1,
                stagger: 0.2,
                ease: 'power4.out'
            }, '<');
        }
    });
}

/**
 * Utility: Wrap each word in a span for animation
 * Filters out empty strings caused by line breaks and extra whitespace
 */
function wrapWordsInSpan(element) {
    const text = element.textContent;
    element.innerHTML = text
        .split(/\s+/)  // Split by any whitespace (spaces, tabs, newlines)
        .filter(word => word.trim() !== '')  // Remove empty strings
        .map(word => `<span class="about-info-word"><span>${word}</span></span>`)
        .join(' ');
}

/**
 * About Reveal section - Telescope Reveal Effect
 */
function initAboutReveal() {
    const root = document.querySelector('.about-reveal');
    if (!root) return;

    const frontImages = root.querySelectorAll('.about-reveal__media-front');
    const smallImages = root.querySelectorAll('.about-reveal__images img');

    const timeline = gsap.timeline({
        scrollTrigger: {
            trigger: root,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
            pin: true,
            onUpdate: (self) => {
                const progress = gsap.parseEase('power1.inOut')(self.progress);
                root.style.setProperty('--progress', progress);
            }
        }
    });

    // Set 3D properties for small images
    gsap.set(smallImages, {
        transformStyle: 'preserve-3d',
        backfaceVisibility: 'hidden',
        force3D: true
    });

    // Animate small images flying towards camera
    timeline.to(smallImages, {
        z: '100vh',
        duration: 1,
        ease: 'power1.inOut',
        stagger: {
            amount: 0.2,
            from: 'center'
        }
    });

    // Scale front images
    timeline.to(
        frontImages,
        {
            scale: 1,
            duration: 1,
            ease: 'power1.inOut',
            delay: 0.1
        },
        0.6
    );

    // Remove blur from front images
    timeline.to(
        frontImages,
        {
            duration: 1,
            filter: 'blur(0px)',
            ease: 'power1.inOut',
            delay: 0.4,
            stagger: {
                amount: 0.2,
                from: 'end'
            }
        },
        0.6
    );

    return timeline;
}
